import React, { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Settings,
  UploadCloud,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Building2,
  UserCircle,
  ListChecks,
  KeyRound,
  Warehouse,
} from "lucide-react";
import {
  setupAPI,
  clearAuthTokens,
  type BackupInfo,
  type WizardBootstrapPayload,
} from "@/lib/api";
import { toast } from "sonner";
import { ArcaAfipWizardPanel } from "@/components/arca/ArcaAfipWizardPanel";

interface FirstRunSetupProps {
  onComplete: () => void;
}

type Phase = "choose" | "new" | "restore";

function backupDate(b: BackupInfo): string | null {
  const raw = (b as unknown as { createdAt?: string }).createdAt ?? b.created_at;
  return raw ?? null;
}

function backupTemplates(b: BackupInfo): number {
  const n = (b as unknown as { pdfTemplateCount?: number }).pdfTemplateCount;
  return n ?? b.pdf_template_count ?? 0;
}

const NEW_STEPS = [
  { id: "welcome", title: "Bienvenida", icon: Settings },
  { id: "admin", title: "Administrador", icon: UserCircle },
  { id: "company", title: "Empresa", icon: Building2 },
  { id: "site", title: "Sucursal y depósito", icon: Warehouse },
  { id: "arca", title: "ARCA/AFIP (opcional)", icon: KeyRound },
  { id: "review", title: "Resumen", icon: ListChecks },
] as const;

export function FirstRunSetup({ onComplete }: FirstRunSetupProps) {
  useEffect(() => {
    clearAuthTokens();
    localStorage.removeItem("currentUser");
  }, []);

  const [phase, setPhase] = useState<Phase>("choose");
  const [newIdx, setNewIdx] = useState(0);
  const [restoreFile, setRestoreFile] = useState<BackupInfo | null>(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [log, setLog] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [doneKind, setDoneKind] = useState<"new" | "restore" | null>(null);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // Default username `admin` is seed-protection (user_is_immortal), not a permission.
  const [adminUser, setAdminUser] = useState("admin");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPass, setAdminPass] = useState("");
  const [adminPass2, setAdminPass2] = useState("");

  const [razonSocial, setRazonSocial] = useState("");
  const [nombreFantasia, setNombreFantasia] = useState("");
  const [cuit, setCuit] = useState("");
  const [domicilio, setDomicilio] = useState("");
  const [localidad, setLocalidad] = useState("");
  const [provincia, setProvincia] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");
  const [telefono, setTelefono] = useState("");
  const [empresaEmail, setEmpresaEmail] = useState("");
  const [condicionIva, setCondicionIva] = useState("Responsable Inscripto");
  const [inicioActividades, setInicioActividades] = useState(today);

  const [sucursalNombre, setSucursalNombre] = useState("Casa central");
  const [sucursalCodigo, setSucursalCodigo] = useState("");
  const [depositoNombre, setDepositoNombre] = useState("Depósito principal");
  const [depositoCodigo, setDepositoCodigo] = useState("");

  const appendLog = (msg: string) => setLog((prev) => [...prev, msg]);

  const resetError = () => {
    setError("");
    setLog([]);
  };

  const validateAdminStep = (): boolean => {
    if (adminUser.trim().length < 3) {
      toast.error("Usuario: mínimo 3 caracteres");
      return false;
    }
    if (adminPass.length < 8) {
      toast.error("Contraseña: mínimo 8 caracteres");
      return false;
    }
    if (adminPass !== adminPass2) {
      toast.error("Las contraseñas no coinciden");
      return false;
    }
    return true;
  };

  const validateCompanyStep = (): boolean => {
    if (!razonSocial.trim() || !cuit.trim() || !domicilio.trim() || !localidad.trim() || !provincia.trim()) {
      toast.error("Completá los campos obligatorios de empresa");
      return false;
    }
    if (!inicioActividades) {
      toast.error("Indicá la fecha de inicio de actividades");
      return false;
    }
    return true;
  };

  const validateSiteStep = (): boolean => {
    if (!sucursalNombre.trim()) {
      toast.error("Indicá el nombre de la sucursal principal");
      return false;
    }
    if (!depositoNombre.trim()) {
      toast.error("Indicá el nombre del depósito principal");
      return false;
    }
    return true;
  };

  const runWizardBootstrap = async () => {
    setRunning(true);
    resetError();
    appendLog("Instalación: empresa, sucursal, depósito, roles, punto de venta interno y métodos de pago…");
    const payload: WizardBootstrapPayload = {
      adminUsername: adminUser.trim(),
      adminPassword: adminPass,
      adminEmail: adminEmail.trim() || undefined,
      company: {
        razonSocial: razonSocial.trim(),
        nombreFantasia: nombreFantasia.trim() || undefined,
        cuit: cuit.trim(),
        domicilio: domicilio.trim(),
        localidad: localidad.trim(),
        provincia: provincia.trim(),
        codigoPostal: codigoPostal.trim() || undefined,
        telefono: telefono.trim() || undefined,
        email: empresaEmail.trim() || undefined,
        condicionIva: condicionIva.trim() || undefined,
        inicioActividades,
      },
      primarySucursal: {
        nombre: sucursalNombre.trim(),
        ...(sucursalCodigo.trim() ? { codigo: sucursalCodigo.trim() } : {}),
      },
      primaryDeposito: {
        nombre: depositoNombre.trim(),
        ...(depositoCodigo.trim() ? { codigo: depositoCodigo.trim() } : {}),
      },
    };
    try {
      const result = await setupAPI.wizardBootstrap(payload);
      if (result.status === "success") {
        appendLog("Templates PDF cargados desde json_templates/");
        appendLog("Instalación finalizada.");
        setDoneKind("new");
        toast.success("Sistema configurado");
      } else {
        throw new Error((result as { message?: string }).message || "Error desconocido");
      }
    } catch (e: unknown) {
      const ex = e as { message?: string; details?: { errors?: Record<string, string> }; errors?: Record<string, string> };
      const fieldErrors = ex.errors ?? ex.details?.errors;
      if (fieldErrors && typeof fieldErrors === "object") {
        const msg = Object.values(fieldErrors).join(" · ");
        appendLog(`ERROR: ${msg}`);
        setError(msg);
        toast.error(msg);
      } else {
        appendLog(`ERROR: ${ex.message || String(e)}`);
        setError(ex.message || String(e));
        toast.error("Error en la instalación");
      }
    } finally {
      setRunning(false);
    }
  };

  const loadBackups = async () => {
    setRunning(true);
    resetError();
    try {
      const list = await setupAPI.listAvailableBackups();
      setBackupsList(list);
      setPhase("restore");
      setRestoreStep("list");
    } catch {
      toast.error("No se pudieron listar los backups (¿hay archivos .tar.gz en data/backups?)");
      setBackupsList([]);
      setPhase("restore");
      setRestoreStep("list");
    } finally {
      setRunning(false);
    }
  };

  const [backupsList, setBackupsList] = useState<BackupInfo[]>([]);
  const [restoreStep, setRestoreStep] = useState<"list" | "confirm">("list");

  const runRestore = async () => {
    if (!restoreFile) return;
    if (restoreConfirm.trim() !== "RESTAURAR") {
      toast.error('Escribí exactamente RESTAURAR para confirmar');
      return;
    }
    setRunning(true);
    resetError();
    appendLog(`Restaurando ${restoreFile.filename}…`);
    try {
      const result = await setupAPI.restoreFromBackup(restoreFile.filename, restoreConfirm.trim());
      if (result.status === "success") {
        appendLog("Restauración completada.");
        setDoneKind("restore");
        toast.success("Backup restaurado");
      } else {
        throw new Error((result as { message?: string }).message || "Error en restauración");
      }
    } catch (e: unknown) {
      const msg = (e as Error).message || String(e);
      appendLog(`ERROR: ${msg}`);
      setError(msg);
      toast.error("Error al restaurar");
    } finally {
      setRunning(false);
    }
  };

  const progressPct = phase === "new" ? Math.round(((newIdx + 1) / NEW_STEPS.length) * 100) : 0;

  if (doneKind) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <Card>
            <CardContent className="py-8 text-center space-y-4">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
              <h3 className="text-xl font-bold">
                {doneKind === "new" ? "Instalación lista" : "Restauración lista"}
              </h3>
              <p className="text-slate-500 text-sm text-left bg-slate-50 dark:bg-slate-800 rounded-lg p-3 font-mono text-xs max-h-40 overflow-y-auto">
                {log.map((l, i) => (
                  <span key={i} className="block">{l}</span>
                ))}
              </p>
              {doneKind === "restore" && (
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Si algo no refleja el backup, reiniciá el contenedor del backend y volvé a ingresar.
                </p>
              )}
              <Button onClick={onComplete} className="w-full">
                Ir al inicio de sesión
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (running && phase === "choose") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
      </div>
    );
  }

  if (phase === "restore") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <div className="w-full max-w-lg space-y-4">
          <div className="text-center mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-3 shadow-lg">
              <UploadCloud className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Restaurar backup</h1>
          </div>

          {restoreStep === "list" && !running && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Archivos disponibles</CardTitle>
                <CardDescription>
                  Solo durante el primer arranque. Los backups deben estar en el servidor (carpeta de backups).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {backupsList.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No hay archivos .tar.gz disponibles.</p>
                ) : (
                  backupsList.map((b) => (
                    <button
                      key={b.filename}
                      type="button"
                      className="w-full text-left p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                      onClick={() => {
                        setRestoreFile(b);
                        setRestoreStep("confirm");
                        setRestoreConfirm("");
                      }}
                    >
                      <p className="font-medium text-sm">{b.name || b.filename}</p>
                      <p className="text-xs text-slate-500">
                        {backupDate(b) ? new Date(backupDate(b)!).toLocaleString("es-AR") : "—"} ·{" "}
                        {(b.size / 1024 / 1024).toFixed(1)} MB
                        {b.label ? ` · ${b.label}` : ""}
                        {backupTemplates(b) ? ` · ${backupTemplates(b)} PDF` : ""}
                      </p>
                    </button>
                  ))
                )}
                <Button variant="ghost" className="w-full" onClick={() => { setPhase("choose"); setRestoreFile(null); }}>
                  ← Volver
                </Button>
              </CardContent>
            </Card>
          )}

          {restoreStep === "confirm" && restoreFile && !running && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg text-red-700 dark:text-red-400">Confirmación fuerte</CardTitle>
                <CardDescription>
                  Se sobrescribirá la base de datos actual con el contenido del backup{" "}
                  <strong>{restoreFile.filename}</strong>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="restore-confirm">Escribí RESTAURAR para continuar</Label>
                  <Input
                    id="restore-confirm"
                    value={restoreConfirm}
                    onChange={(e) => setRestoreConfirm(e.target.value)}
                    placeholder="RESTAURAR"
                    className="mt-1.5"
                    autoComplete="off"
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => { setRestoreStep("list"); setRestoreFile(null); }}>
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700"
                    onClick={runRestore}
                    disabled={restoreConfirm.trim() !== "RESTAURAR"}
                  >
                    Restaurar ahora
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {running && phase === "restore" && (
            <Card>
              <CardContent className="py-10 text-center space-y-4">
                <Loader2 className="h-10 w-10 animate-spin mx-auto text-blue-500" />
                <p className="font-medium">Restaurando… puede tardar varios minutos.</p>
                <div className="text-left text-xs font-mono bg-slate-50 dark:bg-slate-800 rounded p-3 max-h-32 overflow-y-auto">
                  {log.map((l, i) => (
                    <p key={i}>{l}</p>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {error && phase === "restore" && !running && (
            <Card className="border-red-200">
              <CardContent className="py-4 flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500 shrink-0" />
                <div className="text-sm text-red-700">{error}</div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  if (phase === "new") {
    const step = NEW_STEPS[newIdx];
    const StepIcon = step.icon;

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
        <div className="w-full max-w-xl space-y-4">
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-3 shadow-lg">
              <StepIcon className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Configuración inicial</h1>
            <p className="text-slate-500 text-sm mt-1">
              Paso {newIdx + 1} de {NEW_STEPS.length}: {step.title}
            </p>
            <div className="mt-3 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden max-w-xs mx-auto">
              <div className="h-full bg-indigo-500 transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          <Card>
            <CardContent className="pt-6 space-y-4">
              {step.id === "welcome" && (
                <>
                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                    Vas a cargar los <strong>datos mínimos reales</strong> de tu operación: administrador, empresa fiscal,{" "}
                    <strong>sucursal principal</strong> y <strong>depósito principal</strong>. El servidor crea en un solo paso lo demás
                    que la app necesita (roles, métodos de pago, punto de venta interno, plantillas base si faltan).
                  </p>
                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mt-3">
                    <strong>Configuración fiscal ARCA/AFIP</strong> es opcional en el asistente; podés completarla después en{" "}
                    <strong>Opciones</strong>.
                  </p>
                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mt-3">
                    Lo que el sistema agrega automáticamente al finalizar:
                  </p>
                  <ul className="list-disc list-inside text-sm text-slate-600 dark:text-slate-300 space-y-1">
                    <li>Tres <strong>roles</strong> (Administrador, Vendedor, Auditor) y permisos de módulos</li>
                    <li>Un <strong>punto de venta</strong> interno (código PV001) ligado a tu sucursal</li>
                    <li><strong>Cinco métodos de pago</strong> base</li>
                    <li>
                      <strong>Plantillas PDF</strong> desde <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1 rounded">json_templates/</code>{" "}
                      si aún no están en base (no pisa plantillas ya cargadas)
                    </li>
                  </ul>
                </>
              )}

              {step.id === "admin" && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="adm-user">Usuario administrador *</Label>
                    <Input id="adm-user" value={adminUser} onChange={(e) => setAdminUser(e.target.value)} className="mt-1" autoComplete="username" />
                  </div>
                  <div>
                    <Label htmlFor="adm-email">Email (opcional)</Label>
                    <Input id="adm-email" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className="mt-1" autoComplete="email" />
                  </div>
                  <div>
                    <Label htmlFor="adm-pass">Contraseña * (mín. 8)</Label>
                    <Input id="adm-pass" type="password" value={adminPass} onChange={(e) => setAdminPass(e.target.value)} className="mt-1" autoComplete="new-password" />
                  </div>
                  <div>
                    <Label htmlFor="adm-pass2">Repetir contraseña *</Label>
                    <Input id="adm-pass2" type="password" value={adminPass2} onChange={(e) => setAdminPass2(e.target.value)} className="mt-1" autoComplete="new-password" />
                  </div>
                </div>
              )}

              {step.id === "company" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400 border-l-2 border-indigo-400 pl-2">
                    Datos de la <strong>empresa titular del ERP</strong> (razón social, CUIT, domicilio fiscal, etc.). No es un registro del
                    módulo Clientes.
                  </p>
                  <div>
                    <Label htmlFor="rs">Razón social *</Label>
                    <Input id="rs" value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="nf">Nombre fantasía</Label>
                    <Input id="nf" value={nombreFantasia} onChange={(e) => setNombreFantasia(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="cuit">CUIT *</Label>
                    <Input id="cuit" value={cuit} onChange={(e) => setCuit(e.target.value)} className="mt-1" placeholder="20-12345678-9" />
                  </div>
                  <div>
                    <Label htmlFor="dom">Domicilio fiscal *</Label>
                    <Input id="dom" value={domicilio} onChange={(e) => setDomicilio(e.target.value)} className="mt-1" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="loc">Localidad *</Label>
                      <Input id="loc" value={localidad} onChange={(e) => setLocalidad(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="prov">Provincia *</Label>
                      <Input id="prov" value={provincia} onChange={(e) => setProvincia(e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="cp">Código postal</Label>
                      <Input id="cp" value={codigoPostal} onChange={(e) => setCodigoPostal(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="tel">Teléfono</Label>
                      <Input id="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="em">Email empresa</Label>
                    <Input id="em" type="email" value={empresaEmail} onChange={(e) => setEmpresaEmail(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="iva">Condición IVA</Label>
                    <Input id="iva" value={condicionIva} onChange={(e) => setCondicionIva(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="ini">Inicio de actividades *</Label>
                    <DateInput id="ini" value={inicioActividades} onChange={(ymd) => setInicioActividades(ymd)} className="mt-1" />
                  </div>
                </div>
              )}

              {step.id === "site" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400 border-l-2 border-indigo-400 pl-2">
                    Nombres reales de tu <strong>primer local</strong> y del <strong>depósito principal</strong> (podés agregar más después en Opciones).
                    Los códigos son opcionales; si los dejás vacíos se usan <code className="text-xs">SUC001</code> y{" "}
                    <code className="text-xs">DEP001</code>.
                  </p>
                  <div>
                    <Label htmlFor="suc-nom">Nombre sucursal principal *</Label>
                    <Input id="suc-nom" value={sucursalNombre} onChange={(e) => setSucursalNombre(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="suc-cod">Código sucursal (opcional)</Label>
                    <Input id="suc-cod" value={sucursalCodigo} onChange={(e) => setSucursalCodigo(e.target.value)} className="mt-1" placeholder="SUC001" maxLength={20} />
                  </div>
                  <div>
                    <Label htmlFor="dep-nom">Nombre depósito principal *</Label>
                    <Input id="dep-nom" value={depositoNombre} onChange={(e) => setDepositoNombre(e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="dep-cod">Código depósito (opcional)</Label>
                    <Input id="dep-cod" value={depositoCodigo} onChange={(e) => setDepositoCodigo(e.target.value)} className="mt-1" placeholder="DEP001" maxLength={20} />
                  </div>
                </div>
              )}

              {step.id === "arca" && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-500 dark:text-slate-400 border-l-2 border-violet-400 pl-2">
                    <strong>Opcional y no bloqueante:</strong> podés cargar certificados y probar WSAA/WSFE ahora, o
                    usar <strong>Siguiente</strong> sin cambiar nada y completar todo en{" "}
                    <strong>Opciones → ARCA / AFIP</strong>. No afecta plantillas PDF ni la creación del sistema al final.
                  </p>
                  <ArcaAfipWizardPanel variant="firstRun" cuitDraft={cuit.trim()} />
                </div>
              )}

              {step.id === "review" && (
                <div className="space-y-3 text-sm">
                  <p className="text-slate-600 dark:text-slate-300">Revisá los datos antes de crear el sistema:</p>
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-1">
                    <p><strong>Admin:</strong> {adminUser}</p>
                    <p><strong>Empresa:</strong> {razonSocial} — CUIT {cuit}</p>
                    <p><strong>Ubicación:</strong> {domicilio}, {localidad}, {provincia}</p>
                    <p><strong>Sucursal:</strong> {sucursalNombre}{sucursalCodigo.trim() ? ` (${sucursalCodigo.trim()})` : " (código por defecto si vacío)"}</p>
                    <p><strong>Depósito:</strong> {depositoNombre}{depositoCodigo.trim() ? ` (${depositoCodigo.trim()})` : " (código por defecto si vacío)"}</p>
                  </div>
                  <p className="text-slate-500 text-xs">
                    El servidor creará roles, punto de venta interno PV001, métodos de pago y completará plantillas PDF base si hace falta. El <strong>punto de venta fiscal AFIP</strong> lo definís en ARCA/AFIP u Opciones.
                  </p>
                </div>
              )}

              {running && (
                <div className="py-8 text-center space-y-3">
                  <Loader2 className="h-10 w-10 animate-spin mx-auto text-blue-500" />
                  <p className="text-sm">Aplicando configuración…</p>
                  <div className="text-left text-xs font-mono bg-slate-50 dark:bg-slate-800 rounded p-3 max-h-36 overflow-y-auto">
                    {log.map((l, i) => (
                      <p key={i}>{l}</p>
                    ))}
                  </div>
                </div>
              )}

              {error && !running && (
                <div className="flex gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 p-3 rounded-lg">
                  <AlertTriangle className="h-5 w-5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {!running && (
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      if (newIdx === 0) {
                        setPhase("choose");
                      } else {
                        setNewIdx((i) => i - 1);
                      }
                      resetError();
                    }}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {newIdx === 0 ? "Volver" : "Anterior"}
                  </Button>
                  {step.id !== "review" ? (
                    <Button
                      type="button"
                      className="flex-1"
                      onClick={() => {
                        resetError();
                        if (step.id === "admin" && !validateAdminStep()) return;
                        if (step.id === "company" && !validateCompanyStep()) return;
                        if (step.id === "site" && !validateSiteStep()) return;
                        setNewIdx((i) => i + 1);
                      }}
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                      onClick={async () => {
                        resetError();
                        await runWizardBootstrap();
                      }}
                    >
                      Crear sistema
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Settings className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">ABS Gestión</h1>
          <p className="text-slate-500 mt-2">Configuración inicial del sistema</p>
        </div>

        <div className="grid gap-4">
          <Card
            className="cursor-pointer transition-all hover:shadow-lg hover:scale-[1.01]"
            onClick={() => {
              resetError();
              setNewIdx(0);
              setPhase("new");
            }}
          >
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-md flex-shrink-0">
                  <Settings className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle>Instalación nueva</CardTitle>
                  <CardDescription className="mt-1">
                    Administrador, empresa fiscal, sucursal y depósito principales; el servidor completa roles, PV interno, métodos de pago y plantillas base.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card
            className="cursor-pointer transition-all hover:shadow-lg hover:scale-[1.01]"
            onClick={() => {
              resetError();
              loadBackups();
            }}
          >
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center shadow-md flex-shrink-0">
                  <UploadCloud className="h-6 w-6 text-white" />
                </div>
                <div>
                  <CardTitle>Restaurar desde backup</CardTitle>
                  <CardDescription className="mt-1">
                    Elegí un archivo del servidor y confirmá con RESTAURAR. Requiere backups previos en la carpeta del entorno.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        </div>
      </div>
    </div>
  );
}
